// components/SupplierMainLayout.js
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Button, Offcanvas } from "react-bootstrap";
import { BsList } from "react-icons/bs";
import API from "../services/api";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import GstApiDueNotice from "./GstApiDueNotice";
import {
  clearStoredSession,
  getStoredUser,
  redirectToLogin,
  SESSION_USER_UPDATED_EVENT,
  updateStoredUser,
} from "../utils/sessionUser";
import "./PanelMobileChrome.css";

const SupplierMainLayout = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    window.addEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    const syncAuthUser = async () => {
      try {
        const res = await API.get("/auth/me");
        const nextUser = res?.data || {};
        updateStoredUser(nextUser);
        setUser((prev) => ({ ...prev, ...nextUser }));
      } catch (_e) {}
    };
    syncAuthUser();
  }, []);

  const handleLogout = () => {
    clearStoredSession();
    redirectToLogin();
  };

  const routeTitles = {
    "/supplier/dashboard": "Dashboard",
    "/supplier/profile": "Profile",
    "/supplier/dispatch/create": "Create Dispatch",
    "/supplier/dispatch/history": "Dispatch History",
    "/supplier/po-details": "PO Details",
    "/supplier/payment-history": "Payment History",
    "/supplier/certificate": "Certificate",
  };

  const menuItems = [
    { path: "/supplier/dashboard", label: "Dashboard", icon: "space_dashboard" },
    { path: "/supplier/profile", label: "Profile", icon: "account_circle" },
    { path: "/supplier/dispatch/create", label: "Dispatch", icon: "local_shipping" },
    { path: "/supplier/dispatch/history", label: "Dispatch History", icon: "history" },
    { path: "/supplier/po-details", label: "PO Details", icon: "description" },
    { path: "/supplier/payment-history", label: "Payment History", icon: "receipt_long" },
    { path: "/supplier/certificate", label: "Certificate", icon: "workspace_premium" },
  ];
  const visibleMenuItems = menuItems;

  const handleNav = (path) => {
    navigate(path);
    setSidebarVisible(false);
  };

  const isDashboardRoute =
    currentPath === "/supplier" ||
    currentPath === "/supplier/" ||
    currentPath === "/supplier/dashboard";
  const isBottomActive = (prefixes = []) =>
    prefixes.some((p) => currentPath === p || currentPath.startsWith(`${p}/`));
  const currentTitle =
    routeTitles[
      Object.keys(routeTitles).find((path) => currentPath.startsWith(path))
    ] || "";
  const handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/supplier/dashboard");
  };

  const userName = user?.fullName || "Supplier";
  const userInitials = useMemo(
    () =>
      String(userName)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("") || "S",
    [userName]
  );
  const userPhoto = resolveProfilePhoto(
    user?.photo,
    user?.photoUpdatedAt || user?.updatedAt || ""
  );

  return (
    <div
      className="panel-layout-shell"
      style={{
        display: "flex",
        height: "100vh",
        "--role-sidebar-gradient": "linear-gradient(180deg, #581c87 0%, #a855f7 100%)",
        "--role-sidebar-border": "#4c1d95",
        "--role-title-color": "#7e22ce",
        "--panel-accent-rgb": "168, 85, 247",
        "--panel-nav-bg": "#581c87",
        "--panel-nav-border": "rgba(255, 255, 255, 0.16)",
        "--panel-nav-shadow": "0 -10px 30px rgba(88, 28, 135, 0.35)",
        "--panel-nav-active-bg": "#ffffff",
        "--panel-nav-active-color": "#581c87",
        "--panel-nav-active-shadow": "0 2px 8px rgba(88, 28, 135, 0.24)",
      }}
    >
      {/* Sidebar (desktop) */}
      <div
        className="d-none d-md-block"
        style={{ width: "240px", background: "var(--role-sidebar-gradient)", height: "100vh", display: "flex", flexDirection: "column" }}
      >
        <div
          style={{
            color: "white",
            fontSize: "22px",
            fontWeight: "bold",
            padding: "8.5px",
            borderBottom: "2px solid var(--role-sidebar-border)",
            textAlign: "center",
            letterSpacing: "1px",
          }}
        >
          🧾 Lotus Rice Mill Pvt. Ltd.
        </div>
        <div className="d-flex flex-column p-3" style={{ flex: 1, overflowY: "auto" }}>
          <div className="panel-sidebar-menu">
          {visibleMenuItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`panel-sidebar-item ${isBottomActive([item.path]) ? "active" : ""}`}
              onClick={() => handleNav(item.path)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Sidebar Offcanvas (mobile) */}
      <Offcanvas
        show={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="panel-mobile-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>🧾 Lotus Rice Mill Pvt. Ltd.</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: "linear-gradient(180deg, #581c87 0%, #a855f7 100%)", color: "#fff" }}>
          <div className="panel-sidebar-menu">
            {visibleMenuItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${isBottomActive([item.path]) ? "active" : ""}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Topbar + Content */}
      <div className="panel-layout-maincol" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          className="panel-layout-topbar"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            backgroundColor: "var(--app-topbar)",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {!isDashboardRoute && (
              <Button
                variant="light"
                className="d-md-none"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <span className="material-symbols-outlined">arrow_back_ios_new</span>
              </Button>
            )}
            <Button
              variant="light"
              className="d-md-none"
              onClick={() => setSidebarVisible(true)}
            >
              <BsList />
            </Button>
            <div
              className="panel-mobile-title"
              style={{ fontSize: "18px", fontWeight: 600, color: "var(--role-title-color)" }}
            >
              {currentTitle}
            </div>
          </div>

          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              size="sm"
              style={{ fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={userName}
                  style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#e2e8f0",
                    color: "#334155",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {userInitials}
                </span>
              )}
              <span className="panel-mobile-user-name">{userName}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item href="/supplier/profile">Profile</Dropdown.Item>
              <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <GstApiDueNotice />

        <div
          className="panel-layout-content"
          style={{
            flex: 1,
            overflowY: "auto",
            background: "var(--app-bg)",
            padding: "20px",
          }}
        >
          <Outlet />
        </div>

        <div className="panel-mobile-nav" style={{ "--panel-nav-count": 6 }}>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/dashboard"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/dashboard")}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Dashboard</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/dispatch/create"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/dispatch/create")}
          >
            <span className="material-symbols-outlined">local_shipping</span>
            <span>Dispatch</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/dispatch/history"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/dispatch/history")}
          >
            <span className="material-symbols-outlined">history</span>
            <span>History</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/po-details"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/po-details")}
          >
            <span className="material-symbols-outlined">description</span>
            <span>PO</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/payment-history"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/payment-history")}
          >
            <span className="material-symbols-outlined">receipt_long</span>
            <span>Payments</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(["/supplier/profile"]) ? "active" : ""}`}
            onClick={() => navigate("/supplier/profile")}
          >
            <span className="material-symbols-outlined">person</span>
            <span>Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierMainLayout;
